/**
 * `JournalStorage` over IndexedDB.
 *
 * This is the step where "the map is still there after closing the browser" stops being a design
 * intention and becomes true. Everything above it — the store, the sequencing, replay, the
 * recovery assessment — was written against the port; this is the first implementation of that
 * port that survives a process ending.
 *
 * It is held to `__tests__/storage-contract.ts`, the same suite the in-memory adapter passes, so
 * the two cannot drift into disagreeing about what an append or a checkpoint means. What it adds
 * beyond that suite is the half the in-memory one cannot have: rows that come back damaged.
 */
import Dexie from 'dexie';
import { appError } from '@orbit-mind/shared-types';
import { StorageError } from '../errors';
import { createDatabase } from './database';
import { readCheckpointRow, readJournalRows, toCheckpointRow, toJournalRow } from './rows';
/**
 * Is `name` the error that actually happened, anywhere in the wrapping?
 *
 * A bulk write does not reject with the failure — it rejects with a `BulkError` carrying the
 * per-item failures, and a transaction that aborts wraps whatever caused it again. Matching only
 * the outermost name reports every one of them as "storage unavailable", which is the least
 * useful thing that could be said about a full disk.
 */
function causedBy(cause, name) {
    const seen = new Set();
    const walk = (value, depth) => {
        if (depth > 4 || typeof value !== 'object' || value === null || seen.has(value))
            return false;
        seen.add(value);
        const candidate = value;
        if (candidate.name === name)
            return true;
        if (Array.isArray(candidate.failures)) {
            for (const failure of candidate.failures)
                if (walk(failure, depth + 1))
                    return true;
        }
        return walk(candidate.inner, depth + 1) || walk(candidate.cause, depth + 1);
    };
    return walk(cause, 0);
}
/**
 * Turns whatever Dexie threw into something with a code.
 *
 * The three that matter are genuinely different situations for the user: a full disk is theirs to
 * fix, a constraint violation is a bug in our sequencing, and a closed or blocked database is
 * worth retrying. Collapsing them into one message would make all three unactionable.
 */
function translate(operation, cause) {
    if (causedBy(cause, 'QuotaExceededError')) {
        return new StorageError('QUOTA_EXCEEDED', 'no space left for the local journal', {
            details: { operation },
            retryable: false,
            cause,
        });
    }
    if (causedBy(cause, 'ConstraintError')) {
        // A sequence that already exists. Not retryable and not the user's problem: something
        // assigned a number twice, and overwriting would destroy the edit already filed under it.
        return new StorageError('INTERNAL', 'journal sequence already used', {
            details: { operation },
            retryable: false,
            cause,
        });
    }
    return new StorageError('STORAGE_UNAVAILABLE', `local journal ${operation} failed`, {
        details: { operation },
        cause,
    });
}
async function guarded(operation, body) {
    try {
        return await body();
    }
    catch (cause) {
        // A `StorageError` from a nested call already carries its meaning; re-wrapping would bury it.
        throw cause instanceof StorageError ? cause : translate(operation, cause);
    }
}
export function createIndexedDbJournalStorage(db = createDatabase()) {
    /** Every row for a map, ascending by sequence — the compound index is already in that order. */
    function rangeOf(mapId, afterSequence) {
        const index = db.journal.where('[mapId+sequence]');
        return afterSequence === undefined
            ? index.between([mapId, Dexie.minKey], [mapId, Dexie.maxKey]).toArray()
            : index.between([mapId, afterSequence], [mapId, Dexie.maxKey], false, true).toArray();
    }
    return {
        db,
        appendEntries(records) {
            if (records.length === 0)
                return Promise.resolve();
            return guarded('append', async () => {
                // One transaction for the batch. `bulkAdd` rejects a key that already exists, and a
                // rejection inside the transaction aborts it — so a colliding sequence anywhere in the
                // batch leaves none of it written, rather than a journal with a hole in it.
                await db.transaction('rw', db.journal, async () => {
                    await db.journal.bulkAdd(records.map(toJournalRow));
                });
            });
        },
        readEntries(mapId, afterSequence) {
            return guarded('read entries', async () => readJournalRows(await rangeOf(mapId, afterSequence)));
        },
        readPending(mapId) {
            return guarded('read pending', async () => {
                // `[mapId+synced]` answers this without touching a synced row. The explicit sort is
                // because that index orders by its own key, not by sequence, and the outbox has to be
                // sent in the order the edits were made.
                const rows = await db.journal.where('[mapId+synced]').equals([mapId, 0]).sortBy('sequence');
                return readJournalRows(rows);
            });
        },
        markSynced(mapId, sequences) {
            if (sequences.length === 0)
                return Promise.resolve();
            return guarded('mark synced', async () => {
                await db.transaction('rw', db.journal, async () => {
                    // Keys that are not there are ignored rather than counted as a failure: an
                    // acknowledgement arriving after a checkpoint pruned the entry is ordinary.
                    await db.journal.bulkUpdate(sequences.map((sequence) => ({ key: [mapId, sequence], changes: { synced: 1 } })));
                });
            });
        },
        readCheckpoint(mapId) {
            return guarded('read checkpoint', async () => {
                const row = await db.checkpoints.get(mapId);
                if (row === undefined)
                    return undefined;
                const read = readCheckpointRow(row);
                if (read.ok)
                    return read.record;
                // There is no partial answer to a damaged checkpoint. Every entry after it is a delta
                // against it, so returning `undefined` would quietly replay the journal over whatever
                // the caller happened to pass as a base — a document that never existed.
                throw new StorageError('MAP_CORRUPTED', 'the stored checkpoint could not be read back', {
                    details: { mapId, reason: read.reason },
                    retryable: false,
                });
            });
        },
        writeCheckpoint(record) {
            return guarded('write checkpoint', async () => {
                await db.transaction('rw', db.checkpoints, db.journal, async () => {
                    await db.checkpoints.put(toCheckpointRow(record));
                    // Synced *and* superseded, in the same transaction as the write. Pruning afterwards
                    // would, if the process died in between, leave entries that replay over a document
                    // that already contains them.
                    await db.journal
                        .where('[mapId+synced]')
                        .equals([record.mapId, 1])
                        .filter((row) => row.sequence <= record.sequence)
                        .delete();
                });
            });
        },
        clear(mapId) {
            return guarded('clear', async () => {
                await db.transaction('rw', db.journal, db.checkpoints, async () => {
                    await db.journal
                        .where('[mapId+sequence]')
                        .between([mapId, Dexie.minKey], [mapId, Dexie.maxKey])
                        .delete();
                    await db.checkpoints.delete(mapId);
                });
            });
        },
        close() {
            db.close();
        },
    };
}
/** Is IndexedDB usable here at all? Private browsing and some embedded webviews say no. */
export function isIndexedDbAvailable() {
    return typeof indexedDB !== 'undefined';
}
/** The failure to report when it is not, so the caller can say something true to the user. */
export function indexedDbUnavailable() {
    const base = appError('STORAGE_UNAVAILABLE', 'IndexedDB is not available in this browser');
    return new StorageError(base.code, base.message, { retryable: false });
}
//# sourceMappingURL=adapter.js.map